import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../../lib/api";

// v1.24.0: edit ONE question video — what the viewer sees (question, options, explanation) and hears (intro, cue,
// answer voice-over, outro). Save updates the script; the MP4 changes only when you press Re-render on the row.
const L = "ABCD";
const words = (t) => String(t || "").trim().split(/\s+/).filter(Boolean).length;

function Field({ label, value, onChange, rows = 2, testid }) {
  return (
    <label className="block space-y-0.5">
      <span className="text-[10px] font-mono uppercase text-zinc-400">{label}</span>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid}
                className="w-full rounded border border-zinc-300 px-2 py-1 text-sm" />
    </label>
  );
}

export function VideoScriptEditor({ video, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/videos/${video.id}/script`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [video.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="px-3 py-2 text-xs text-zinc-500" data-testid="video-editor-loading">Loading script…</div>;
  if (!d.editable) return <p className="px-3 py-2 text-xs text-amber-700" data-testid="video-editor-not-editable">{d.reason}</p>;
  const setItem = (i, patch) => setD((cur) => ({ ...cur, items: cur.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));
  const setOpt = (i, k, val) => setItem(i, { q_options: d.items[i].q_options.map((o, j) => (j === k ? val : o)) });
  const save = async () => {
    setBusy(true);
    try {
      const body = { intro_script: d.intro_script, outro_script: d.outro_script,
        items: d.items.map((it) => ({ q_label: it.q_label, q_stem: it.q_stem, q_options: it.q_options, q_correct: it.q_correct,
                                      q_explanation: it.q_explanation || "", cue_script: it.cue_script, explain_script: it.explain_script })) };
      const { data } = await api.put(`/admin/videos/${video.id}/script`, body);
      toast.success(data.render_note, { duration: 8000 });
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
    setBusy(false);
  };
  const voice = words(d.intro_script) + words(d.outro_script) + d.items.reduce((n, it) => n + words(it.cue_script) + words(it.explain_script), 0);
  return (
    <div className="mx-1 mb-2 rounded-md border border-sky-300 bg-sky-50/40 p-3 space-y-3" data-testid={`video-editor-${video.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 text-xs text-zinc-700"><span className="font-semibold">Edit video · {d.title}</span> — {d.items.length} question{d.items.length > 1 ? "s" : ""} · voice-over {voice} words.
          {d.script_edited && <span className="ml-1 text-sky-800">Edited {new Date(d.edited_at).toLocaleString()}.</span>} Changes reach the MP4 only when you press Re-render.</p>
      </div>
      {d.intro_script != null && <Field label="Intro voice-over (spoken before the first question)" value={d.intro_script} onChange={(v) => setD({ ...d, intro_script: v })} testid="video-editor-intro" />}
      <ol className="space-y-3">
        {d.items.map((it, i) => (
          <li key={it.q_label} className="rounded border border-zinc-200 bg-white p-3 grid gap-3 md:grid-cols-2" data-testid={`video-editor-item-${it.q_label}`}>
            <div className="space-y-2">
              <p className="text-[10px] font-mono uppercase text-zinc-500">{it.q_label} · on screen</p>
              <Field label="Question" value={it.q_stem} onChange={(v) => setItem(i, { q_stem: v })} rows={3} testid={`video-editor-${it.q_label}-stem`} />
              <div className="space-y-1">
                {it.q_options.map((o, k) => (
                  <div key={k} className="flex items-center gap-2">
                    <button type="button" onClick={() => setItem(i, { q_correct: k })} data-testid={`video-editor-${it.q_label}-correct-${k}`}
                            className={`h-6 w-6 shrink-0 rounded-full text-[11px] font-bold ${it.q_correct === k ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"}`}
                            title="Mark as the correct answer">{L[k]}</button>
                    <input value={o} onChange={(e) => setOpt(i, k, e.target.value)} data-testid={`video-editor-${it.q_label}-option-${k}`}
                           className="w-full rounded border border-zinc-300 px-2 py-1 text-xs" />
                  </div>
                ))}
              </div>
              <Field label="Explanation shown on the answer frame" value={it.q_explanation} onChange={(v) => setItem(i, { q_explanation: v })} rows={3} testid={`video-editor-${it.q_label}-explanation`} />
            </div>
            <div className="space-y-2">
              <p className="text-[10px] font-mono uppercase text-zinc-500">{it.q_label} · voice-over</p>
              {it.cue_script != null && <Field label="Cue (spoken as the question appears)" value={it.cue_script} onChange={(v) => setItem(i, { cue_script: v })} rows={1} testid={`video-editor-${it.q_label}-cue`} />}
              <Field label={`Answer voice-over · ${words(it.explain_script)} words`} value={it.explain_script} onChange={(v) => setItem(i, { explain_script: v })} rows={6} testid={`video-editor-${it.q_label}-explain`} />
            </div>
          </li>
        ))}
      </ol>
      {d.outro_script != null && <Field label="Outro voice-over (spoken after the last answer)" value={d.outro_script} onChange={(v) => setD({ ...d, outro_script: v })} testid="video-editor-outro" />}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} disabled={busy} data-testid="video-editor-cancel-btn" className="px-3 py-1.5 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="video-editor-save-btn" className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white ${busy ? "bg-zinc-400" : "bg-zinc-900 hover:bg-zinc-700"}`}>{busy ? "Saving…" : "Save script"}</button>
      </div>
    </div>
  );
}
